/**
 * Custom Variant Gallery
 *
 * Swaps the product media gallery with variant-specific images
 * from the custom.custom_gallery metafield. Only activates on
 * products where at least one variant has this metafield populated.
 */
(function () {
  const dataEl = document.querySelector('[id^="CustomGalleryData-"]');
  if (!dataEl) return;

  let config;
  try {
    config = JSON.parse(dataEl.textContent);
  } catch (e) {
    console.error('[CustomVariantGallery] Failed to parse config', e);
    return;
  }

  const sectionId = config.sectionId;
  const variantsWithGallery = config.variantsWithGallery.map(String);
  let activeVariantId = String(config.currentVariantId);
  let customGalleryActive = false;

  const state = (window.customVariantGalleryState = window.customVariantGalleryState || {});
  state[sectionId] = {
    activeVariantId: activeVariantId,
    variantsWithGallery: variantsWithGallery,
    isActive: variantsWithGallery.indexOf(activeVariantId) !== -1,
  };

  function publishState() {
    state[sectionId] = {
      activeVariantId: activeVariantId,
      variantsWithGallery: variantsWithGallery,
      isActive: customGalleryActive,
    };
  }

  const sectionEl = document.getElementById(`shopify-section-${sectionId}`);
  if (!sectionEl) return;

  const slider = sectionEl.querySelector('[id^="Slider-Gallery-"]');
  const thumbnailSlider = sectionEl.querySelector('[id^="GalleryThumbnails-"] .product-gallery-thumbs__track');
  const stackedThumbnails = sectionEl.querySelector('[id^="ProductThumbnails-"]');
  const sliderButtons = sectionEl.querySelector('product-gallery .slider-buttons');
  const prevBtn = sliderButtons && sliderButtons.querySelector('.slider-button--prev');
  const nextBtn = sliderButtons && sliderButtons.querySelector('.slider-button--next');
  const counterCurrent = sectionEl.querySelector('.slider-counter--current');
  const counterTotal = sectionEl.querySelector('.slider-counter--total');

  function hasCustomGallery(variantId) {
    return variantsWithGallery.includes(String(variantId));
  }

  function getCustomSlides(variantId) {
    if (!slider) return [];
    return slider.querySelectorAll(`.custom-gallery-item[data-custom-gallery-variant="${variantId}"]`);
  }

  function getVisibleCustomSlides() {
    if (!slider) return [];
    return slider.querySelectorAll('.custom-gallery-item:not(.custom-gallery-item--hidden)');
  }

  function getRegularSlides() {
    if (!slider) return [];
    return slider.querySelectorAll('.product__media-item:not(.custom-gallery-item)');
  }

  function getCustomThumbs(variantId, container) {
    if (!container) return [];
    return container.querySelectorAll(`.custom-gallery-thumb[data-custom-gallery-variant="${variantId}"]`);
  }

  function getRegularThumbs(container) {
    if (!container) return [];
    return container.querySelectorAll(':scope > :not(.custom-gallery-thumb)');
  }

  function scrollSliderTo(targetEl) {
    slider.style.setProperty('scroll-behavior', 'auto', 'important');
    slider.style.setProperty('scroll-snap-type', 'none', 'important');
    slider.scrollLeft = targetEl.offsetLeft;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        slider.style.removeProperty('scroll-behavior');
        slider.style.removeProperty('scroll-snap-type');
      });
    });
  }

  function activateCustomGallery(variantId) {
    customGalleryActive = true;
    publishState();

    // Hide all regular slides
    getRegularSlides().forEach(el => {
      el.classList.add('custom-gallery-item--hidden');
      el.classList.remove('is-active');
    });

    // Hide all custom slides first
    slider.querySelectorAll('.custom-gallery-item').forEach(el => {
      el.classList.add('custom-gallery-item--hidden');
      el.classList.remove('is-active');
    });

    // Show this variant's custom slides
    const customSlides = getCustomSlides(variantId);
    customSlides.forEach((el, i) => {
      el.classList.remove('custom-gallery-item--hidden');
      if (i === 0) el.classList.add('is-active');
    });

    // Scroll slider to first custom slide
    if (customSlides.length > 0) {
      scrollSliderTo(customSlides[0]);
    }

    // Update slider counter
    updateCounter(customSlides.length);

    // Update thumbnails (thumbnail_slider layout)
    if (thumbnailSlider) {
      getRegularThumbs(thumbnailSlider).forEach(el => el.classList.add('custom-gallery-item--hidden'));
      thumbnailSlider.querySelectorAll('.custom-gallery-thumb').forEach(el => {
        el.classList.add('custom-gallery-item--hidden');
        el.classList.remove('is-active');
        el.removeAttribute('aria-current');
      });
      getCustomThumbs(variantId, thumbnailSlider).forEach((el, i) => {
        el.classList.remove('custom-gallery-item--hidden');
        if (i === 0) {
          el.classList.add('is-active');
          el.setAttribute('aria-current', 'true');
        }
      });
    }

    // Update thumbnails (stacked layout)
    if (stackedThumbnails) {
      getRegularThumbs(stackedThumbnails).forEach(el => el.classList.add('custom-gallery-item--hidden'));
      stackedThumbnails.querySelectorAll('.custom-gallery-thumb').forEach(el => {
        el.classList.add('custom-gallery-item--hidden');
        el.classList.remove('is-active');
      });
      getCustomThumbs(variantId, stackedThumbnails).forEach((el, i) => {
        el.classList.remove('custom-gallery-item--hidden');
        if (i === 0) el.classList.add('is-active');
      });
    }
  }

  function deactivateCustomGallery() {
    customGalleryActive = false;
    publishState();

    // Hide all custom slides
    slider.querySelectorAll('.custom-gallery-item').forEach(el => {
      el.classList.add('custom-gallery-item--hidden');
      el.classList.remove('is-active');
    });

    // Show all regular slides
    getRegularSlides().forEach(el => {
      el.classList.remove('custom-gallery-item--hidden');
    });

    // Activate the first regular slide
    const firstRegular = slider.querySelector('.product__media-item:not(.custom-gallery-item)');
    if (firstRegular) {
      firstRegular.classList.add('is-active');
      scrollSliderTo(firstRegular);
    }

    // Count visible regular slides
    const visibleCount = slider.querySelectorAll('.product__media-item:not(.custom-gallery-item):not(.product__media-item--variant):not([style*="display: none"])').length;
    updateCounter(visibleCount);

    // Restore regular thumbnails
    if (thumbnailSlider) {
      thumbnailSlider.querySelectorAll('.custom-gallery-thumb').forEach(el => el.classList.add('custom-gallery-item--hidden'));
      getRegularThumbs(thumbnailSlider).forEach((el, i) => {
        el.classList.remove('custom-gallery-item--hidden');
        if (i === 0) {
          el.classList.add('is-active');
          el.setAttribute('aria-current', 'true');
        }
      });
    }

    if (stackedThumbnails) {
      stackedThumbnails.querySelectorAll('.custom-gallery-thumb').forEach(el => el.classList.add('custom-gallery-item--hidden'));
      getRegularThumbs(stackedThumbnails).forEach(el => el.classList.remove('custom-gallery-item--hidden'));
    }
  }

  function updateCounter(count) {
    if (counterCurrent) counterCurrent.textContent = '1';
    if (counterTotal) counterTotal.textContent = String(count);
    if (sliderButtons) {
      sliderButtons.classList.toggle('hidden', count < 2);
    }
  }

  function navigateCustomSlide(direction) {
    const slides = getVisibleCustomSlides();
    if (slides.length === 0) return;

    let currentIndex = -1;
    slides.forEach((el, i) => {
      if (el.classList.contains('is-active')) currentIndex = i;
    });

    let nextIndex;
    if (direction === 'next') {
      nextIndex = currentIndex + 1;
      if (nextIndex >= slides.length) nextIndex = 0;
    } else {
      nextIndex = currentIndex - 1;
      if (nextIndex < 0) nextIndex = slides.length - 1;
    }

    // Update slides
    slides.forEach(el => el.classList.remove('is-active'));
    slides[nextIndex].classList.add('is-active');
    scrollSliderTo(slides[nextIndex]);

    // Update counter
    if (counterCurrent) counterCurrent.textContent = String(nextIndex + 1);

    // Update thumbnails
    const thumbContainer = thumbnailSlider || stackedThumbnails;
    if (thumbContainer) {
      const thumbs = thumbContainer.querySelectorAll('.custom-gallery-thumb:not(.custom-gallery-item--hidden)');
      thumbs.forEach(el => {
        el.classList.remove('is-active');
        el.removeAttribute('aria-current');
      });
      if (thumbs[nextIndex]) {
        thumbs[nextIndex].classList.add('is-active');
        thumbs[nextIndex].setAttribute('aria-current', 'true');
        thumbs[nextIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }

  // Intercept prev/next buttons when custom gallery is active
  function onSliderButtonClick(e) {
    if (!customGalleryActive) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    const direction = e.currentTarget.name === 'next' ? 'next' : 'prev';
    navigateCustomSlide(direction);
  }

  if (prevBtn) prevBtn.addEventListener('click', onSliderButtonClick, true);
  if (nextBtn) nextBtn.addEventListener('click', onSliderButtonClick, true);

  function onVariantChange(event) {
    if (!event.detail || !event.detail.variant) return;
    const newVariantId = String(event.detail.variant.id);
    if (newVariantId === activeVariantId) return;
    activeVariantId = newVariantId;

    if (hasCustomGallery(activeVariantId)) {
      activateCustomGallery(activeVariantId);
    } else {
      deactivateCustomGallery();
    }
  }

  // Wire up thumbnail clicks for custom gallery thumbs
  function setupThumbnailClicks(container) {
    if (!container) return;
    container.addEventListener('click', function (e) {
      const thumb = e.target.closest('.custom-gallery-thumb');
      if (!thumb) return;
      e.preventDefault();
      e.stopPropagation();

      const variantId = thumb.dataset.customGalleryVariant;
      const index = parseInt(thumb.dataset.customGalleryIndex, 10);

      // Update thumbnail active states
      container.querySelectorAll('.custom-gallery-thumb').forEach(el => {
        el.classList.remove('is-active');
        el.removeAttribute('aria-current');
      });
      thumb.classList.add('is-active');
      thumb.setAttribute('aria-current', 'true');

      // Find and scroll to the corresponding slide
      const targetSlide = slider.querySelector(
        `.custom-gallery-item[data-custom-gallery-variant="${variantId}"][data-custom-gallery-index="${index}"]`
      );
      if (!targetSlide) return;

      slider.querySelectorAll('.product__media-item').forEach(el => el.classList.remove('is-active'));
      targetSlide.classList.add('is-active');
      scrollSliderTo(targetSlide);

      // Update counter
      if (counterCurrent) counterCurrent.textContent = String(index + 1);
    });
  }

  // Initialize: apply correct state on page load
  if (hasCustomGallery(activeVariantId)) {
    activateCustomGallery(activeVariantId);
  }

  // Listen for variant changes
  document.addEventListener('variant:change', onVariantChange);

  // Set up thumbnail click handlers
  setupThumbnailClicks(thumbnailSlider);
  setupThumbnailClicks(stackedThumbnails);
})();
